import { AbstractService } from '@/services/core/serviceFactory';
import { patientsGraphqlService, statusGraphqlService, usersGraphqlService } from '@/services/api';

export interface ProgramUpdateDto {
  programTypeId: number;
  programStatusId: number;
  workflowStatusId: number;
  currentWorkflowId?: number | null;
  appointmentId?: number | null;
  communicationTypeId?: number | null;
  appointmentType?: 1 | 2; // 1=Virtual, 2=In-Person
  appointmentDate?: string; // YYYY-MM-DD
  appointmentTime?: string; // HH:mm:ss
  appointmentStartTime?: string; // HH:mm:ss
  appointmentEndTime?: string; // HH:mm:ss
  resourceId?: number;
  resourceTimezone?: string;
  resourceAvailabilityDayTimeId?: number;
  facilityAvailabilityDayTimeId?: number;
  communicationAttempt?: number;
  comment: string;
  groupId?: number | null;
}

export interface ESEnrollmentPayload {
  programs: ProgramUpdateDto[];
}

// Legacy payload structure for backwards compatibility with other roles (EA, CS, PE)
export interface LegacyESEnrollmentPayload {
  global_flag: boolean;
  comment: string;
  deferred_queue: boolean;
  provider_id?: number;
  ccm_status?: number;
  rpm_status?: number;
  ccm_appointmentDate?: string;
  rpm_appointmentDate?: string;
  patient_clinic_timezone?: string;
}

export interface ESEnrollmentResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface UserProfile {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phoneNumber: string;
  type: string;
  healthCareCenterId: number | null;
  theme: string;
  isActive: boolean;
  entityId: number | null;
  fullName: string;
  isStaff: boolean;
  newCC: string | null;
  showActivityDuration: boolean | null;
  ccWeeklyHr: number | null;
}

/**
 * ES Enrollment Service - Handles REST API calls for patient enrollment operations
 * Segregated from ES services to features folder for reusability
 * Uses proper three-layer architecture: GraphQL services + REST apiClient + business logic
 */
export class ESEnrollmentService extends AbstractService {
  /**
   * Save patient enrollment data via REST API using patient_id (new structure)
   */
  async savePatientEnrollment(
    userId: string,
    patientId: number,
    payload: ESEnrollmentPayload
  ): Promise<ESEnrollmentResponse> {
    this.requireAuth();

    try {
      console.log('ESEnrollmentService: Saving patient enrollment data (new structure)');
      const response = await this.apiClient.post(
        `/api/ngpep/${userId}/enrollment/${patientId}`,
        payload,
        this.context.token
      );
      
      if (response.success) {
        return {
          success: true,
          message: 'Patient enrollment saved successfully',
          data: response.data
        };
      } else {
        return {
          success: false,
          message: response.error || 'Failed to save patient enrollment'
        };
      }
    } catch (error) {
      console.error('ESEnrollmentService: Error saving patient enrollment:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to save patient enrollment'
      };
    }
  }

  /**
   * Save patient enrollment data via REST API (legacy structure for backwards compatibility)
   */
  async savePatientEnrollmentLegacy(
    userId: string,
    patientId: number,
    payload: LegacyESEnrollmentPayload
  ): Promise<ESEnrollmentResponse> {
    this.requireAuth();

    try {
      console.log('ESEnrollmentService: Saving patient enrollment data (legacy structure)');
      const response = await this.apiClient.post(
        `/api/pep/${userId}/enrollment/${patientId}`,
        payload,
        this.context.token
      );
      
      if (response.success) {
        return {
          success: true,
          message: 'Patient enrollment saved successfully',
          data: response.data
        };
      } else {
        return {
          success: false,
          message: response.error || 'Failed to save patient enrollment'
        };
      }
    } catch (error) {
      console.error('ESEnrollmentService: Error saving patient enrollment:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to save patient enrollment'
      };
    }
  }

  /**
   * Lock patient by group via REST API
   */
  async lockPatientByGroup(userId: string, groupId: number): Promise<ESEnrollmentResponse> {
    this.requireAuth();

    try {
      console.log('ESEnrollmentService: Locking patient by group');
      const response = await this.apiClient.post(
        `/api/pep/${userId}/lockPatientByGroup/${groupId}`,
        {},
        this.context.token
      );
      
      if (response.success) {
        return {
          success: true,
          message: 'Patient locked successfully',
          data: response.data
        };
      } else {
        return {
          success: false,
          message: response.error || 'Failed to lock patient'
        };
      }
    } catch (error) {
      console.error('ESEnrollmentService: Error locking patient:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to lock patient'
      };
    }
  }

  /**
   * Unlock patient via REST API using patient_id
   */
  async unlockPatient(userId: string, patientId: number): Promise<ESEnrollmentResponse> {
    this.requireAuth();

    try {
      console.log('ESEnrollmentService: Unlocking patient');
      const response = await this.apiClient.post(
        `/api/pep/${userId}/unlockPatient/${patientId}`,
        {},
        this.context.token
      );
      
      if (response.success) {
        return {
          success: true,
          message: 'Patient unlocked successfully',
          data: response.data
        };
      } else {
        return {
          success: false,
          message: response.error || 'Failed to unlock patient'
        };
      }
    } catch (error) {
      console.error('ESEnrollmentService: Error unlocking patient:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to unlock patient'
      };
    }
  }

  /**
   * Get visible patient data via REST API
   * Replaces the previous GraphQL query to pep_vw_patient_workflow_user_assignments
   */
  async getVisiblePatient(): Promise<{
    success: boolean;
    data?: any;
    error?: string;
    message?: string;
  }> {
    this.requireAuth();

    try {
      console.log('[ESEnrollmentService] Fetching visible patient for user:', this.context.userId);
      const response = await this.apiClient.get(
        `/api/ngpep/${this.context.userId}/visiblePatient`,
        this.context.token
      );
      
      if (response.success && response.data) {
        // The API response has structure: { data: {...}, success: true, message: "..." }
        // Extract the inner data object - don't use || fallback since data can be null
        const visiblePatientData = response.data.data;
        const apiMessage = response.data.message;
        console.log('[ESEnrollmentService] Visible patient response:', visiblePatientData);
        
        // Handle case where API returns success but data is null (no patient available)
        if (visiblePatientData === null || visiblePatientData === undefined) {
          return {
            success: true,
            data: null,
            message: apiMessage || 'No visible patient available at this time'
          };
        }
        
        return {
          success: true,
          data: visiblePatientData,
          message: apiMessage
        };
      } else {
        return {
          success: false,
          error: response.error || 'Failed to fetch visible patient'
        };
      }
    } catch (error) {
      console.error('ESEnrollmentService: Error getting visible patient:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get visible patient'
      };
    }
  }

  /**
   * @deprecated Use getVisiblePatient() instead
   * Get patient data via GraphQL service using assigned_user_id from workflow assignments view
   */
  async getPatientData() {
    this.requireAuth();

    try {
      return await patientsGraphqlService.getESPatientData(
        this.context.hasuraToken!, 
        parseInt(String(this.context.userId))
      );
    } catch (error) {
      console.error('ESEnrollmentService: Error getting patient data:', error);
      throw new Error('Failed to get patient data');
    }
  }

  /**
   * Get patient assignment data by userId from workflow assignments view
   * Used when loading a specific patient (e.g., from PE/CS dashboard or global search)
   * Now uses only logged-in userId in the variables payload
   */
  async getPatientAssignmentByPatientId(userId: number) {
    this.requireAuth();

    try {
      console.log('[ESEnrollmentService] Fetching patient assignment with userId:', userId);
      return await patientsGraphqlService.getPatientAssignmentByPatientId(
        this.context.hasuraToken!,
        userId
      );
    } catch (error) {
      console.error('ESEnrollmentService: Error getting patient assignment:', error);
      throw new Error('Failed to get patient assignment');
    }
  }

  /**
   * Get full patient enrollment details via REST API using patient_id
   * Transforms the API response to match the expected patient data structure
   */
  async getPatientEnrollmentDetails(patientId: number): Promise<any> {
    this.requireAuth();

    try {
      console.log('[ESEnrollmentService] Fetching patient enrollment details for patient:', patientId);
      console.log('[ESEnrollmentService] API URL:', `/api/ngpep/${this.context.userId}/enrollmentDetails/${patientId}`);
      console.log('[ESEnrollmentService] Has token:', !!this.context.token);
      
      const response = await this.apiClient.get(
        `/api/ngpep/${this.context.userId}/enrollmentDetails/${patientId}`,
        this.context.token
      );
      
      console.log('[ESEnrollmentService] API response status:', response.status, 'success:', response.success);
      
      if (response.success && response.data) {
        // The API response has structure: { data: {...}, success: true }
        // Extract the inner data object
        const apiData = response.data.data || response.data;
        const demographic = apiData.patientDemographic || {};
        const ccmAttempts = apiData.enrollmentAttemptsByProgramType?.CCM || [];
        const rpmAttempts = apiData.enrollmentAttemptsByProgramType?.RPM || [];
        
        console.log('[ESEnrollmentService] Raw API response:', {
          hasEnrollmentAttempts: !!apiData.enrollmentAttemptsByProgramType,
          enrollmentKeys: apiData.enrollmentAttemptsByProgramType ? Object.keys(apiData.enrollmentAttemptsByProgramType) : [],
          fullEnrollmentData: apiData.enrollmentAttemptsByProgramType
        });

        // Map API response to expected PatientData format
        const transformedData = {
          first_name: demographic.firstName || '',
          last_name: demographic.lastName || '',
          middle_name: demographic.middleName || '',
          dob: demographic.dob || '',
          gender: demographic.gender || '',
          mobile_phone_number: demographic.mobileNumber || demographic.phoneNumber || '',
          home_phone_number: demographic.homeNumber || '',
          email: demographic.email || '',
          address_line_1: demographic.addressLineOne || '',
          address_line_2: demographic.addressLineTwo || '',
          city: demographic.city || '',
          state: demographic.state || '',
          zip_code: demographic.zipCode || '',
          clinic_id: demographic.clinicId || null,
          clinic_name: demographic.clinicName || '',
          // Use timeZone from patientDemographic, fallback to US/Eastern
          patient_clinic_timezone: demographic.timeZone || 'US/Eastern',
          provider_name: demographic.providerName || '',
          provider_id: demographic.providerId || 0,
          clinic_facility_id: demographic.clinicFacilityId || 0,
          primary_dx: demographic.primaryDx || '',
          secondary_dx: demographic.secondaryDx || '',
          primary_insurance_provider: demographic.primaryInsuranceProvider || '',
          primary_insurance_id: demographic.primaryInsuranceNumber || '',
          secondary_insurance_provider: demographic.secondaryInsuranceProvider || '',
          secondary_insurance_id: demographic.secondaryInsuranceNumber || '',
          emr_id: demographic.emrId || '',
          emr_patient_master_id: demographic.emrPatientMasterId || 0,
          emr_name: demographic.emrName || '',
          emergency_contact_name: demographic.emergencyContactName || '',
          emergency_contact_phone: demographic.emergencyContactPhone || '',
          medical_record_number: demographic.medicalRecordNumber || '',
          // Enrollment attempts as status updates (legacy)
          ccm_status_updates: ccmAttempts,
          rpm_status_updates: rpmAttempts,
          // Include full enrollment attempts by program type for dynamic tabs
          enrollmentAttemptsByProgramType: apiData.enrollmentAttemptsByProgramType || {},
          // Additional fields (legacy compatibility)
          emergency_first_name: demographic.emergencyContactName || '',
          emergency_mobile_phone: demographic.emergencyContactPhone || '',
          // Program eligibility flags from API response
          rpmEligible: apiData.rpmEligible || false,
          pcmEligible: apiData.pcmEligible || false,
          ccmEligible: apiData.ccmEligible || false,
        };
        
        console.log('[ESEnrollmentService] Transformed data includes enrollment attempts:', {
          hasEnrollmentAttempts: !!transformedData.enrollmentAttemptsByProgramType,
          enrollmentKeys: transformedData.enrollmentAttemptsByProgramType ? Object.keys(transformedData.enrollmentAttemptsByProgramType) : []
        });

        return {
          success: true,
          data: transformedData
        };
      } else {
        return {
          success: false,
          message: response.error || 'Failed to fetch patient enrollment details'
        };
      }
    } catch (error) {
      console.error('ESEnrollmentService: Error fetching patient enrollment details:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to fetch patient enrollment details'
      };
    }
  }

  /**
   * Get CCM status updates via GraphQL service using patient_id
   */
  async getCCMStatusUpdates(patientId: number) {
    this.requireAuth();

    try {
      console.log('ESEnrollmentService: Getting CCM status updates');
      return await statusGraphqlService.getCCMStatusUpdates(this.context.hasuraToken!, patientId);
    } catch (error) {
      console.error('ESEnrollmentService: Error getting CCM status updates:', error);
      throw new Error('Failed to get CCM status updates');
    }
  }

  /**
   * Get ES names mapping for CCM updates table using existing usersGraphqlService
   */
  async getESNamesMapping(): Promise<Record<number, string>> {
    this.requireAuth();

    try {
      console.log('ESEnrollmentService: Getting ES names mapping');
      const result = await usersGraphqlService.getEnrollmentSpecialists(this.context.hasuraToken!);
      
      // Create a mapping object from user ID to ES name
      const mapping: Record<number, string> = {};
      if (result.data?.pep_vw_enrollment_specialists) {
        result.data.pep_vw_enrollment_specialists.forEach(es => {
          mapping[es.hbox_user_id] = es.es_name;
        });
      }
      
      return mapping;
    } catch (error) {
      console.error('ESEnrollmentService: Error getting ES names mapping:', error);
      return {};
    }
  }

  /**
   * Validate enrollment payload
   * Note: Updated for new payload structure
   */
  validateEnrollmentPayload(payload: ESEnrollmentPayload): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!payload.programs || payload.programs.length === 0) {
      errors.push('At least one program is required');
    }

    // Validate each program
    if (payload.programs) {
      payload.programs.forEach((program, index) => {
        if (!program.programTypeId) {
          errors.push(`Program ${index + 1}: Program Type ID is required`);
        }
        if (!program.programStatusId) {
          errors.push(`Program ${index + 1}: Program Status ID is required`);
        }
        if (!program.workflowStatusId) {
          errors.push(`Program ${index + 1}: Workflow Status ID is required`);
        }
        if (!program.comment || program.comment.trim().length === 0) {
          errors.push(`Program ${index + 1}: Comment is required`);
        }
        if (program.comment && program.comment.length > 500) {
          errors.push(`Program ${index + 1}: Comment must be 500 characters or less`);
        }
        
        // Validate appointment fields
        if (program.appointmentType && !program.appointmentDate) {
          errors.push(`Program ${index + 1}: Appointment date is required when scheduling`);
        }
        if (program.appointmentStartTime && program.appointmentEndTime) {
          if (program.appointmentStartTime >= program.appointmentEndTime) {
            errors.push(`Program ${index + 1}: End time must be after start time`);
          }
        }
        // Virtual appointment requires resource
        if (program.appointmentType === 1 && !program.resourceId) {
          errors.push(`Program ${index + 1}: Resource/Provider is required for virtual appointments`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Create enrollment payload with default values (removed as it's now built in operations layer)
   */

  /**
   * Check if enrollment is allowed based on patient status
   */
  isEnrollmentAllowed(patientData: any): {
    allowed: boolean;
    reason?: string;
  } {
    if (!patientData) {
      return {
        allowed: false,
        reason: 'Patient data not found'
      };
    }

    // Check if patient already has enrolled status
    if (patientData.ccm_status === 'Enrolled' || patientData.rpm_status === 'Enrolled') {
      return {
        allowed: false,
        reason: 'Patient is already enrolled in a program'
      };
    }

    // Check required patient information
    const requiredFields = ['first_name', 'last_name', 'mobile_phone_number', 'email'];
    const missingFields = requiredFields.filter(field => 
      !patientData[field] || patientData[field] === 'n/a'
    );

    if (missingFields.length > 0) {
      return {
        allowed: false,
        reason: `Missing required patient information: ${missingFields.join(', ')}`
      };
    }

    return { allowed: true };
  }

  /**
   * Check if transfer is enabled based on status and comment
   */
  isTransferEnabled(selectedStatus: string, comment: string): boolean {
    return selectedStatus === 'Consent' && comment.trim().length > 0;
  }

  /**
   * Validate date string format
   */
  private isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  }
}

export const esEnrollmentService = new ESEnrollmentService();